import createAssetsFromClipboardData from './createAssetsFromClipboardData';
import { useAssetRegistry } from '../hooks/useAssetRegistry';
import * as React from 'react';
import { isTextInputElement } from 'owa-timestream-asset-sdk';
import type {
    AssetOrAssetsFromClipboardData,
    AssetOrAssetsOrPromiseFromClipboardData,
} from 'owa-timestream-asset-sdk-types';

export default function usePasteAsset(canvasBodyRef: React.RefObject<HTMLDivElement>) {
    const assetRegistry = useAssetRegistry();

    function onPaste(e: ClipboardEvent) {
        // VSO 99044: Ignore the paste event if the current element with focus is not a descendant
        // of the canvas body.
        const isActiveElementDescendantOfCanvasBody: boolean = canvasBodyRef.current!.contains(
            document.activeElement
        );

        if (
            !e.clipboardData ||
            isTextInputElement(e.target as HTMLInputElement) ||
            !isActiveElementDescendantOfCanvasBody
        ) {
            return;
        }

        const getData = e.clipboardData.getData.bind(e.clipboardData);
        const items = e.clipboardData.items;

        // $TODO: if multiple descriptors say they can create an asset out of the clipboard data
        // then we should show a context menu to let the user pick the best format.
        // The idea was to let the asset create the payload and we would then show the options.
        // But right now Notes breaks the pattern, since calling its createAsset function
        // ends up creating a sticky note, even if the user were to pick a different asset type.
        // We'll need to change that somehow. In principle, the note should only be created
        // once the asset is mounted by the asset view, but we can't carry the clipboard data
        // in the payload, since it cannot be saved in the SDS item.
        let assetOrAssetsOrPromise: AssetOrAssetsOrPromiseFromClipboardData<unknown> = null;
        for (const asset of assetRegistry.pastePrecedence) {
            assetOrAssetsOrPromise = filter(asset.createAssetFromClipboardData!(getData, items));
            if (assetOrAssetsOrPromise) {
                break;
            }
        }

        if (assetOrAssetsOrPromise) {
            e.preventDefault();
            e.stopPropagation();

            if (isPromise(assetOrAssetsOrPromise)) {
                assetOrAssetsOrPromise.then(createAssetOnPageCoordinatesHelper);
            } else {
                createAssetOnPageCoordinatesHelper(assetOrAssetsOrPromise);
            }
        }
    }

    React.useEffect(() => {
        document.body.addEventListener('paste', onPaste);
        return () => document.body.removeEventListener('paste', onPaste);
    });
}

function filter(assetOrAssetsOrPromise: AssetOrAssetsOrPromiseFromClipboardData<unknown>) {
    if (isPromise(assetOrAssetsOrPromise)) {
        return assetOrAssetsOrPromise;
    }

    if (Array.isArray(assetOrAssetsOrPromise)) {
        const validItems = assetOrAssetsOrPromise.filter(x => x);
        return validItems.length ? validItems : null;
    }

    return assetOrAssetsOrPromise;
}

function isPromise<T>(p: T | Promise<T>): p is Promise<T> {
    return Promise.resolve(p) === p;
}

function createAssetOnPageCoordinatesHelper(assetOrAssets: AssetOrAssetsFromClipboardData) {
    const { clientWidth, clientHeight } = document.documentElement;
    let x = clientWidth / 4;
    let y = clientHeight / 4;
    createAssetsFromClipboardData('Paste', assetOrAssets, x, y);
}
